import React from 'react'
import { TableData } from 'contexts'
import useDownLoadMaterialTable from './useDownLoadMaterialTable'

type CsvExportHookInput = {
  title: string
  data: Array<TableData>
  onFetchAllPages?: (
    pageSize?: number,
    onProgress?: (info: { loaded: number; total: number }) => void,
    signal?: AbortSignal,
  ) => Promise<Array<TableData>>
  displayAlert: (message: string, severity: 'error' | 'success' | 'info' | 'warning') => void
}

type CsvExportState = {
  loaded: number
  total: number
}

const DEFAULT_PROGRESS: CsvExportState = { loaded: 0, total: 0 }

const useCsvExport = ({
  title,
  data,
  onFetchAllPages,
  displayAlert,
}: CsvExportHookInput) => {
  const { csvFileName, generateCsv } = useDownLoadMaterialTable({ title })
  const [isExporting, setIsExporting] = React.useState(false)
  const [exportProgress, setExportProgress] = React.useState<CsvExportState>(DEFAULT_PROGRESS)
  const abortControllerRef = React.useRef<AbortController | null>(null)

  const triggerDownload = React.useCallback(
    (csvData: string) => {
      if (typeof window === 'undefined' || typeof document === 'undefined') {
        return
      }

      const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' })
      const url = window.URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = url
      anchor.download = csvFileName || 'data.csv'
      document.body.appendChild(anchor)
      anchor.click()
      document.body.removeChild(anchor)
      window.URL.revokeObjectURL(url)
    },
    [csvFileName],
  )

  const handleDownload = React.useCallback(async () => {
    if (isExporting) return

    const supportsAbortController = typeof AbortController !== 'undefined'
    const controller = onFetchAllPages && supportsAbortController ? new AbortController() : null

    try {
      setIsExporting(true)
      setExportProgress(DEFAULT_PROGRESS)
      abortControllerRef.current = controller

      const rows = onFetchAllPages
        ? await onFetchAllPages(
            undefined,
            ({ loaded, total }) => {
              setExportProgress({ loaded, total })
            },
            controller?.signal,
          )
        : data

      const csv = generateCsv(rows)
      triggerDownload(csv)
    } catch (error) {
      const isAbortError = error instanceof Error && error.name === 'AbortError'
      if (!isAbortError) {
        // eslint-disable-next-line no-console
        console.error(error)
        displayAlert('Erreur lors de la génération du CSV', 'error')
      }
    } finally {
      abortControllerRef.current = null
      setIsExporting(false)
      setExportProgress(DEFAULT_PROGRESS)
    }
  }, [data, displayAlert, generateCsv, isExporting, onFetchAllPages, triggerDownload])

  const cancelExport = React.useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
    setIsExporting(false)
    setExportProgress(DEFAULT_PROGRESS)
  }, [])

  return {
    isExporting,
    exportProgress,
    handleDownload,
    cancelExport,
  }
}

export default useCsvExport
